import type { ChatMessage, Citation, WidgetConfig } from "@shared/types";

type StreamHandlers = {
  onMeta?: (payload: { citations: Citation[]; messageId: number }) => void;
  onChunk?: (payload: { text: string }) => void;
  onDone?: (payload: { message: ChatMessage }) => void;
};

export async function apiFetch<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {})
    }
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({ error: "Request failed" }))) as {
      error?: string;
    };
    throw new Error(payload.error || "Request failed");
  }

  return response.json() as Promise<T>;
}

export async function createSession(tenantSlug: string, locale: string, visitorSessionId?: string) {
  return apiFetch<{
    session: { id: number; visitorSessionId: string; locale: string };
    messages: ChatMessage[];
  }>("/api/public/session", {
    method: "POST",
    body: JSON.stringify({
      tenantSlug,
      locale,
      visitorSessionId
    })
  });
}

export async function getWidgetConfig(tenantSlug: string) {
  return apiFetch<{
    tenant: { id: number; name: string; slug: string };
    widgetConfig: WidgetConfig;
  }>(`/api/public/config/${encodeURIComponent(tenantSlug)}`);
}

export async function readSseStream(response: Response, handlers: StreamHandlers) {
  if (!response.body) {
    throw new Error("Streaming is not supported in this environment");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushBlock = (block: string) => {
    const eventMatch = block.match(/^event:\s*(.+)$/m);
    const dataMatch = block.match(/^data:\s*(.+)$/m);
    if (!eventMatch || !dataMatch) {
      return;
    }
    const eventName = eventMatch[1].trim();
    const payload = JSON.parse(dataMatch[1]) as unknown;
    if (eventName === "meta") {
      handlers.onMeta?.(payload as { citations: Citation[]; messageId: number });
    } else if (eventName === "chunk") {
      handlers.onChunk?.(payload as { text: string });
    } else if (eventName === "done") {
      handlers.onDone?.(payload as { message: ChatMessage });
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() || "";
    for (const block of blocks) {
      flushBlock(block);
    }
  }

  if (buffer.trim()) {
    flushBlock(buffer);
  }
}
